import { recognitionMetricKeys, type CaptureProfile, type NormalizedRect } from '@/domain/types'

const colorMetrics = ['hp', 'mp', 'exp'] as const

/** The previously selected attributes row takes precedence over its saved HUD fallback. */
export function migrateManualRegions(profile: CaptureProfile): CaptureProfile {
  if (!profile.attributeSources) return profile
  const { attributeSources, ...rest } = profile
  const roiOverrides = { ...profile.roiOverrides }
  for (const [key, source] of Object.entries(attributeSources)) {
    const rect = source?.enabled ? source.region?.rect : undefined
    if (rect && [rect.x, rect.y, rect.width, rect.height].every(Number.isFinite)
      && rect.x >= 0 && rect.y >= 0 && rect.width > 0 && rect.height > 0
      && rect.x + rect.width <= 1.001 && rect.y + rect.height <= 1.001) {
      roiOverrides[key as keyof typeof attributeSources] = { ...rect }
    }
  }
  return { ...rest, roiOverrides }
}

function cloneRect(rect: NormalizedRect): NormalizedRect {
  return { x: rect.x, y: rect.y, width: rect.width, height: rect.height }
}

export function cloneCaptureProfile(profile: CaptureProfile): CaptureProfile {
  const roiOverrides: CaptureProfile['roiOverrides'] = {}
  for (const metric of recognitionMetricKeys) {
    const rect = profile.roiOverrides[metric]
    if (rect) roiOverrides[metric] = cloneRect(rect)
  }

  const colorCalibration: CaptureProfile['colorCalibration'] = {}
  for (const metric of colorMetrics) {
    const color = profile.colorCalibration[metric]
    if (color) colorCalibration[metric] = [color[0], color[1], color[2]]
  }

  return {
    ...profile,
    ...(profile.attributeSources ? { attributeSources: Object.fromEntries(Object.entries(profile.attributeSources).map(([metric, source]) => [metric, {
      ...source, ...(source.region ? { region: { rect: cloneRect(source.region.rect), valueRect: cloneRect(source.region.valueRect),
        anchor: cloneRect(source.region.anchor), signature: [...source.region.signature] } } : {}),
    }])) } : {}),
    ...(profile.autoRois ? { autoRois: Object.fromEntries(Object.entries(profile.autoRois).map(([metric, region]) => [metric, {
      ...region, rect: cloneRect(region.rect),
      ...(region.textRect ? { textRect: cloneRect(region.textRect) } : {}),
      ...(region.barRect ? { barRect: cloneRect(region.barRect) } : {}),
    }])) } : {}),
    hudRect: cloneRect(profile.hudRect),
    roiOverrides,
    colorCalibration,
    recognition: {
      ...profile.recognition,
      ...(profile.recognition.metricCaptureFps
        ? { metricCaptureFps: { ...profile.recognition.metricCaptureFps } }
        : {}),
      ...(profile.recognition.metricRecognitionIntervalsMs
        ? { metricRecognitionIntervalsMs: { ...profile.recognition.metricRecognitionIntervalsMs } }
        : {}),
    },
  }
}
